import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {RootState} from "../store";
import {Difficulty, TypesenseResponse} from "../../../../utils/models/TypesenseResponse";
import {ExploreV2State} from "../../../explore_v2/store/modules/ExploreV2Module";
import {MeasurementPrefUtility} from "../../../../utils/helpers/MeasurementPref";

export class CountryRegionTrailsState {

  pageCount: number = 1

  trailTags: string[] = []

  typesenseResponse: TypesenseResponse | null = null
  typesensePageCount: number = 6

  static distanceRangeMin = 0.0
  static distanceRangeMax = 150.0

  selectedDistanceRangeMin: number = 0.0
  selectedDistanceRangeMax: number | null = null

  static elevationRangeMin = 0.0
  static elevationRangeMax = 2500.0

  static durationRangeMin = 0.0
  static durationRangeMax = 250.0

  selectedDurationRangeMin: number = 0.0
  selectedDurationRangeMax: number | null = null

  selectedDifficulties: string[] = []
  selectedTrailTags: string[] = []
  selectedElevations: string[] = []
  selectedMinRating: number | null = null

  searchString: string = ""

}

const actions: ActionTree<CountryRegionTrailsState, RootState> = {
  initialiseStore(store) {
    store.dispatch('getTypesenseResponse')
  },
  async getTypesenseResponse(store) {
    const typeSenseResponse = await TypesenseResponse.get(
      "*", store.getters.toFilterString, null, store.state.typesensePageCount
    )
    store.commit('setTypesenseResponse', typeSenseResponse)
  }
}

const mutations: MutationTree<CountryRegionTrailsState> = {
  setTypesensePageCount(state, typesensePageCount: number) {
    state.typesensePageCount = typesensePageCount
  },
  clearRating(state) {
    state.selectedMinRating = null
  },
  setRating(state, rating: number) {
    state.selectedMinRating = rating
  },
  removeTrailTag(state, tag: string) {
    state.selectedTrailTags.splice(state.selectedTrailTags.indexOf(tag), 1)
  },
  addTrailTag(state, tag: string) {
    state.selectedTrailTags.push(tag)
  },
  setSelectedTrailTags(state, tags: string[]) {
    state.selectedTrailTags = tags
  },
  setTrailTags(state, tags: string[]) {
    state.trailTags = tags
  },
  setTypesenseResponse(state, typesenseResponse: TypesenseResponse) {
    state.typesenseResponse = typesenseResponse
  },
  setDistanceRangeMax(state, mxDistance: number) {
    state.selectedDistanceRangeMax = mxDistance
  },
  setDistanceRangeMin(state, mxDistance: number) {
    state.selectedDistanceRangeMin = mxDistance
  },
  restore(state, savedState) {
    Object.assign(state, savedState)
  },
  setDifficulties(state, difficulties: Difficulty[]) {
    state.selectedDifficulties = difficulties
  },
  setElevations(state, elevationDifficulties: Difficulty[]) {
    state.selectedElevations = elevationDifficulties
  },
  incrementPageCount(state) {
    state.pageCount++
  }
}

const getters: GetterTree<CountryRegionTrailsState, RootState> = {
  countToShow(state): number {
    return state.pageCount * 3
  },
  combineAndUniqueifyDifficulties(state) {
    const combinedDifficulties = [...state.selectedDifficulties, ...state.selectedElevations];
    let a = combinedDifficulties.concat();
    for (let i = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if (a[i] === a[j])
          a.splice(j--, 1);
      }
    }
    return a;
  },
  toFilterString(state, getters): string {
    let filterString = ""
    filterString += `&&country_ids:${window.countryId}`
    if (window.regionName) {
      filterString += "&&region_name:" + window.regionName
    }
    if (state.selectedDistanceRangeMax) {
      filterString += "&&distance:["
      filterString += `${state.selectedDistanceRangeMin * 1000}..`
      if (state.selectedDistanceRangeMax! == ExploreV2State.distanceRangeMax) {
        filterString += `${Math.round((250000 * 1000))}`
      } else {
        filterString += `${Math.round((state.selectedDistanceRangeMax! * 1000))}`
      }
      filterString += "]"
    }
    if (state.selectedMinRating) {
      filterString += `&&average_rating:>=${Math.round(state.selectedMinRating)}`
    }
    let difficulties = getters.combineAndUniqueifyDifficulties
    if (difficulties.length > 0) {
      filterString += "&&("
      for (let i = 0; i < difficulties.length; i++) {
        filterString += `difficulty:=\`${difficulties[i]}\``
        if (i < difficulties.length - 1) {
          filterString += " || "
        }
      }
      filterString += ")"
    }
    if (state.selectedTrailTags.length > 0) {
      filterString += "&&tags:["
      for (const tag of state.selectedTrailTags) {
        if (tag == state.selectedTrailTags[state.selectedTrailTags.length - 1]) {
          filterString += `${tag}]`
        } else {
          filterString += `${tag}, `
        }
      }
    }
    if (state.selectedDurationRangeMin && state.selectedDurationRangeMax) {
      filterString += "&&duration_minutes:["
      filterString += `${Math.round(state.selectedDurationRangeMin * 60)}..`
      if (state.selectedDurationRangeMax! == ExploreV2State.durationRangeMax) {
        filterString += `${Math.round(250000000 * 60)}`
      } else {
        filterString += `${Math.round(state.selectedDurationRangeMax * 60)}`
        filterString += "\((selectedDurationRange.1! * 60).roundToInt())"
      }
      filterString += "]"
    }
    return filterString
  },
  hasDistanceFilter(state): boolean {
    return (state.selectedDistanceRangeMax ?? ExploreV2State.distanceRangeMax) < ExploreV2State.distanceRangeMax
  },
  hasDifficultyFilter(state): boolean {
    return state.selectedDifficulties.length != 0
  },
  hasSelectedTags(state) {
    return state.selectedTrailTags.length > 0
  },
  reversedActiveTrails(state) {
    return state.selectedTrailTags.slice().reverse()
  },
  hasElevationsFilter(state): boolean {
    return state.selectedElevations.length != 0
  },
  filterSelectedOptionsMaxDistance(state) {
    let measurementPref = MeasurementPrefUtility.preference()
    // @ts-ignore
    return measurementPref.bigUnitFormatter(state.filterSelectedOptions.selectedDistanceRangeMax).toString()
  },
  hasFilter(state) {
    return state.selectedTrailTags.length > 0 ||
      state.selectedDistanceRangeMax != null ||
      state.selectedElevations.length > 0 ||
      state.selectedDifficulties.length > 0 ||
      state.selectedMinRating != null
  },
}

const state = new CountryRegionTrailsState();

export default class CountryRegionTrailsModule implements Module<CountryRegionTrailsState, RootState> {
  namespaced = true;
  state = state;
  actions = actions;
  mutations = mutations;
  getters = getters;
}
